import { FC, ReactElement, Ref } from 'react';

import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { ProductPrice, Wrapper } from '../../../../../components';
import { ProductCallToAction, ProductLabels } from '../../../../../compositions';
import { FormImageOption } from '../../../../../entities/@forms/Form/Form';
import { ProductVariant } from '../../../../../entities/@products/ProductVariant/ProductVariant';
import { AppRoute, appRoutes } from '../../../../../entities/Routing/Routing';
import { replaceUrlParamKeysWithValues } from '../../../../../helpers/url';
import { useDeviceWidth, useTrans } from '../../../../../hooks';
import { ProductVariantsSkeletons } from '../../skeletons';
import { ProductInformation } from '../ProductInformation/ProductInformation';
import { ProductVariantSelector } from '../ProductVariantSelector/ProductVariantSelector';

import './ProductSidebar.scss';

interface ProductSidebarProps {
    cartIsLoading: boolean;
    wishlistIsLoading: boolean;
    variantsAreLoading: boolean;
    hasScrolledPassedCallToActionsButtons: boolean;
    isInWishlist: boolean;
    productVariant: ProductVariant;
    variants?: FormImageOption[];
    stockQuantity: number;
    inCartQuantity: number;
    onAddToCart: () => void;
    onWishlistButtonClick: () => void;
    callToActionsRef: Ref<HTMLDivElement>;
    className?: string;
}

export const ProductSidebar: FC<ProductSidebarProps> = ({
    cartIsLoading,
    wishlistIsLoading,
    variantsAreLoading,
    hasScrolledPassedCallToActionsButtons,
    isInWishlist,
    stockQuantity,
    inCartQuantity,
    onAddToCart,
    onWishlistButtonClick,
    productVariant,
    variants = [],
    callToActionsRef,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { isMobile } = useDeviceWidth();

    const floatingCallToActionClassNames = classNames('product-sidebar__floating-call-to-action', {
        'product-sidebar__floating-call-to-action--is-visible': hasScrolledPassedCallToActionsButtons,
    });

    const brandDetailPath = trans(appRoutes[AppRoute.brandDetail].path);
    const brandDetailUrl = replaceUrlParamKeysWithValues(brandDetailPath, {
        slug: productVariant.brand?.href,
    });

    return (
        <div className={`product-sidebar ${className}`}>
            <ProductLabels
                isInStock={productVariant.inStock}
                price={productVariant.price}
                className="product-sidebar__labels"
            />

            <div className="product-sidebar__brand-wrapper">
                {productVariant.brand && (
                    <Link to={brandDetailUrl} className="product-sidebar__brand-link">
                        <h3 className="product-sidebar__brand">
                            {productVariant.brand.label}
                        </h3>
                    </Link>
                )}
            </div>

            <h1 className="product-sidebar__name">
                {productVariant.name}
            </h1>

            <ProductPrice
                {...productVariant.price}
                className="product-sidebar__price"
                oldPriceClassName="product-sidebar__price--is-old"
            />

            <div className="product-sidebar__notices">
                <p className="product-sidebar__notice">
                    {productVariant.deliveryNotice || trans('containers.productDetailRenderer.unknownDeliveryNotice')}
                </p>
            </div>

            {variantsAreLoading && (
                <ProductVariantsSkeletons className="product-sidebar__variants-selector" />
            )}

            {(!variantsAreLoading && variants.length > 1) && (
                <ProductVariantSelector
                    variants={variants}
                    currentSlug={productVariant.relativeUrl}
                    className="product-sidebar__variants-selector"
                />
            )}

            {!isMobile && (
                <div ref={callToActionsRef}>
                    <ProductCallToAction
                        cartIsLoading={cartIsLoading}
                        wishlistIsLoading={wishlistIsLoading}
                        isInWishlist={isInWishlist}
                        stockQuantity={stockQuantity}
                        inCartQuantity={inCartQuantity}
                        onAddToCart={onAddToCart}
                        onWishlistButtonClick={onWishlistButtonClick}
                        className="product-sidebar__call-to-action"
                    />
                </div>
            )}

            {!isMobile && (
                <ProductInformation productVariant={productVariant} />
            )}

            <div className={floatingCallToActionClassNames}>
                <Wrapper className="product-sidebar__call-to-action-wrapper">
                    <ProductCallToAction
                        cartIsLoading={cartIsLoading}
                        wishlistIsLoading={wishlistIsLoading}
                        isInWishlist={isInWishlist}
                        stockQuantity={stockQuantity}
                        inCartQuantity={inCartQuantity}
                        onAddToCart={onAddToCart}
                        onWishlistButtonClick={onWishlistButtonClick}
                    />
                </Wrapper>
            </div>
        </div>
    );
};
